import type { components } from '@connected/api-client/api.generated';
import type { RadioGroupOption } from '@connected/form-fields-v2/components/form/radio-group';

import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validators.generated';
import { useAuthentication } from '@connected/api-client/hooks/use-authentication';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { useAppForm } from '@connected/form-fields-v2/hooks/use-app-form';
import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { Button } from '@connected/ui/components/button';
import { Popout, PopoutBody, PopoutContent, PopoutHeader, PopoutTitle } from '@connected/ui/components/popout';
import { Skeleton } from '@connected/ui/components/skeleton';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Edit, PlusIcon, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import z from 'zod';

import { DataTable } from '@/components/data-table';
import { LoadingPage } from '@/components/loading-page';
import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/query-client';
import { showConfirm } from '@/stores/confirmer';

const validTabsArray = [
    'DistributionBoard',
    'PhotovoltaicInverter',
    'Machine',
    'Other',
    // 'Scope12InspectionPlanDocument',
    // 'Scope12VisualInspection',
    // 'Scope12AdditionalInspectionPoints',
    // 'Scope12DocumentationDCSide',
    // 'Scope12ManualAndDataSpecifications',
    // 'Scope12GeneralRemarks',
    // 'Scope12PVInstallationPowerLine',
    // 'Scope12InspectionExecution',
    // 'Scope12DocumentationACSide',
    // 'Scope12MeasurementsAndTesting',
    // 'Scope12VisualInspectionOtherParts',
    // 'Scope12RoofConstructionCalculation',
    'FinalCircuit',
] as const;

export const Route = createFileRoute(
    '/_auth/dashboard/organizations/$organizationId/settings/visual-inspections/',
)({
    component: RouteComponent,
    validateSearch: z.object({
        tab: z.enum(validTabsArray).optional(),
        search: z.string().optional(),
    }),
});

type CheckListItems = components['schemas']['ListElectricalCheckListItemQueryResultItem'];
type CheckListSingleItem = components['schemas']['ListElectricalCheckListItemQueryResultItem'] & {
    ElectricalCheckListId: string | null;
    Description: string | null;
};

function ChecklistModal({
    isOpen,
    setIsOpen,
    checklist,
    tabs,
    ElectricalCheckListId,
}: {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    checklist: CheckListItems | null;
    tabs: RadioGroupOption<CheckListItems['Kind']>[];
    ElectricalCheckListId: string | null;
}) {
    const { t } = useTranslation('dashboard');
    const { tab } = Route.useSearch();
    const [loading, setIsLoading] = useState<boolean>(false);

    const createChecklistItem = api.useMutation(
        'post',
        '/api/v2/electrical/checklists/items/create',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(portaalQueryOptions.checklists.listChecklist({ electricalCheckListId: ElectricalCheckListId ?? '' })).then(() => {
                    toast.success(t('dashboard.settings.checklist.message.createSuccess'));
                    setIsOpen(false);
                },
                );
            },
            onError: (error) => {
                logger.error(
                    {
                        system: 'CreateElectricalCheckListItemCommandResult',
                        error,
                    },
                    { apiHttpMethod: 'Post', apiUrl: '/api/v2/electrical/checklists/items/create' },
                );
                toast.error(error?.Message ?? t('dashboard.general.messages.error'));
            },
            onMutate: () => setIsLoading(true),
        },
    );
    const updateChecklist = api.useMutation(
        'put',
        '/api/v2/electrical/checklists/items/edit',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(portaalQueryOptions.checklists.listChecklist({ electricalCheckListId: ElectricalCheckListId ?? '' })).then(() => {
                    toast.success(t('dashboard.settings.checklist.message.editSuccess'));
                    setIsOpen(false);
                },
                );
            },
            onError: (error) => {
                logger.error(
                    {
                        system: 'EditElectricalCheckListItemCommandResult',
                        error,
                    },
                    { apiHttpMethod: 'Put', apiUrl: '/api/v2/electrical/checklists/items/edit' },
                );

                toast.error(error?.Message ?? t('dashboard.general.messages.error'));
            },
            onMutate: () => setIsLoading(true),
        },
    );

    const DEFAULT_VALUES: CheckListSingleItem = {
        ElectricalCheckListId: ElectricalCheckListId ?? '',
        ElectricalCheckListItemId: checklist?.ElectricalCheckListItemId,
        Description: checklist?.Description ?? null,
        Kind: tab as 'DistributionBoard' | 'PhotovoltaicInverter' | 'Machine' | 'Other' | 'FinalCircuit',
    };

    const form = useAppForm(
        {
            defaultValues: DEFAULT_VALUES,
            onSubmit: async (value) => {
                if (checklist) {
                    await updateChecklist.mutateAsync(
                        {
                            body: {
                                Description: value.value.Description ?? '',
                                ElectricalCheckListItemId: checklist?.ElectricalCheckListItemId ?? '',
                                Order: checklist?.Order ?? 1,
                            },
                        },
                    );
                }
                else {
                    await createChecklistItem.mutateAsync({
                        body: {
                            Description: value.value.Description ?? '',
                            ElectricalCheckListId: ElectricalCheckListId ?? '',
                            Kind: value.value.Kind ?? 'DistributionBoard',
                        },
                    });
                }
            },
        },
    );

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        form.handleSubmit();
    }

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            <PopoutContent>
                <PopoutHeader>
                    <PopoutTitle>
                        {!checklist ? (t('dashboard.settings.checklist.create.title')) : t('dashboard.settings.checklist.update.title')}
                    </PopoutTitle>
                </PopoutHeader>
                <PopoutBody>
                    <form onSubmit={handleSubmit}>
                        <form.AppField
                            name="Description"
                            validators={{
                                onChange: validators.createElectricalCheckListCommand.description,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label={t('dashboard.settings.checklist.table.item')} />
                                    <field.Input placeholder={t('dashboard.settings.checklist.table.itemPlaceholder')} />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        >
                        </form.AppField>
                        { !checklist
                            && (
                                <form.AppField
                                    name="Kind"
                                    validators={{
                                        onChange: z.enum(['DistributionBoard', 'PhotovoltaicInverter', 'Machine', 'Other', 'FinalCircuit']),
                                    }}
                                    children={field => (
                                        <field.Label asDiv>
                                            <field.Label.Title label={t('dashboard.settings.checklist.table.typeRecommendation')} />
                                            <field.Popover closeOnChange={true}>
                                                <field.RadioGroup options={tabs}>
                                                    <field.Popover.Trigger asChild>
                                                        <field.RadioGroup.Trigger placeholder={t('dashboard.users.table.rolePlaceholder')} />
                                                    </field.Popover.Trigger>
                                                    <field.Popover.Content className="h-auto min-h-30 [&>div]:flex-col [&>div]:items-start [&>div]:gap-6" matchTriggerWidth>
                                                        <field.RadioGroup options={tabs} />
                                                    </field.Popover.Content>
                                                </field.RadioGroup>
                                            </field.Popover>
                                            <field.ValidationError />
                                        </field.Label>
                                    )}
                                />
                            )}
                        <div className="w-full flex flex-row gap-2 mt-5">
                            <Button className="w-full" onClick={() => setIsOpen(false)} variant="outline">{t('dashboard.cancel')}</Button>
                            <Button disabled={loading} type="submit" variant="default" className="w-full">{t('dashboard.create')}</Button>
                        </div>
                    </form>
                </PopoutBody>
            </PopoutContent>
        </Popout>
    );
}

function RouteComponent() {
    const { organizationId } = Route.useParams();
    const { t } = useTranslation('dashboard');
    const search = Route.useSearch();

    const tabsKind: RadioGroupOption<CheckListItems['Kind']>[] = [

        {
            value: 'DistributionBoard',
            label: t('dashboard.settings.recommendations.tabs.distributionBoard'),
        },
        {
            value: 'FinalCircuit',
            label: t('dashboard.settings.checklist.table.headers.machine'),
        },
        // {
        //     value: 'PhotovoltaicInverter',
        //     label: t('dashboard.settings.checklist.table.headers.photovoltaicInverter'),
        // },
        // {
        //     value: 'Other',
        //     label: t('dashboard.settings.checklist.table.headers.other'),
        // },
    ];

    const [isOpenModal, setOpenModal] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<CheckListItems['Kind']>(tabsKind[0].value);
    const [item, setItem] = useState<CheckListItems | null>(null);

    const { currentUser } = useAuthentication();

    const getActiveChecklist = useQuery(
        elektraAppQueryOptions.organisation.management.checkListOptions({
            organisationId: organizationId,
        }),
    );

    const activeChecklist = useQuery(
        {
            ...portaalQueryOptions.checklists.listChecklist({
                electricalCheckListId: getActiveChecklist?.data?.CheckLists[0]?.ElectricalCheckListId ?? '',
            }),
            enabled: !!getActiveChecklist.data?.CheckLists[0]?.ElectricalCheckListId,
        },
    );

    const switchTab = (tab: CheckListItems['Kind']) => {
        setActiveTab(tab);
    };

    const removeChecklistItem = api.useMutation('delete', '/api/v2/electrical/checklists/items/delete', {
        onSuccess: () => {
            queryClient.invalidateQueries(portaalQueryOptions.checklists.listChecklist({ electricalCheckListId: getActiveChecklist.data?.CheckLists[0]?.ElectricalCheckListId ?? '' })).then(() => {
                toast.success(t('dashboard.settings.checklist.message.removeSuccess'));
            },
            );
        },
        onError: (error) => {
            logger.error(
                {
                    system: 'DeleteElectricalCheckListItemCommandResult',
                    error,
                },
                { apiHttpMethod: 'Delete', apiUrl: '/api/v2/electrical/checklists/items/delete' },
            );
            toast.error(error?.Message ?? t('dashboard.general.messages.error'));
        },
    });

    const columns = useMemo(() => [
        {
            accessorKey: 'Aanbeveling',
            header: () => (
                <DataTable.Header>
                    {t('dashboard.organizations.table.headers.name')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: CheckListItems } }) => (
                <DataTable.Cell variant="medium">
                    {row?.original?.Description}
                </DataTable.Cell>
            ),
        },
        {
            id: 'actionModal',
            meta: {
                sticky: true,
                width: 10,
            },
            header: '',
            cell: ({ row }: { row: { original: CheckListItems } }) => {
                return (
                    <div className="flex gap-1 items-center justify-end ml-auto">
                        <Button
                            variant="secondary"
                            size="icon"
                            className="flex gap-2 w-fit h-fit justify-start p-2 bg-background-primary text-foreground hover:bg-background-secondary"
                            onClick={async () => {
                                if (await showConfirm(t('dashboard.settings.recommendations.message.confirmDelete'))) {
                                    await removeChecklistItem.mutateAsync({
                                        params: {
                                            query: {
                                                ElectricalCheckListItemId: row?.original?.ElectricalCheckListItemId ?? '',
                                            },
                                        },
                                    });
                                }
                            }}
                        >
                            <Trash2 className="text-text-primary/40 size-5" />
                        </Button>
                        <Button
                            variant="secondary"
                            size="icon"
                            className="flex gap-2 w-fit h-fit justify-start p-2 bg-background-primary text-foreground hover:bg-background-secondary"
                            onClick={() => {
                                setOpenModal(true);
                                setItem(row?.original);
                            }}
                        >
                            <Edit className="text-text-primary/40 size-5" />
                        </Button>
                    </div>
                );
            },
        },
    ], [removeChecklistItem, t]);

    const tableColumns = useMemo(
        () => activeChecklist.isLoading
            ? columns.map(column => ({ ...column, cell: () => <Skeleton className="h-8 w-full" /> }))
            : columns,
        [activeChecklist.isLoading, columns],
    );

    const tableData = useMemo<CheckListItems[]>(
        () => {
            if (activeChecklist.isError) {
                toast.error(activeChecklist.error?.Message);
                return [];
            }
            const searchTerm = search.search?.toLowerCase() || '';

            return (
                activeChecklist.data?.Items
                    ?.filter((norm) => {
                        if (norm.Kind !== activeTab)
                            return false;
                        if (!searchTerm)
                            return true;

                        return [
                            norm.Description || '',
                        ].some(field => field?.toString().toLowerCase().includes(searchTerm));
                    })
                    .map(norm => ({
                        Description: norm.Description ?? '',
                        Order: norm.Order ?? null,
                        OrganizationId: organizationId,
                        ElectricalCheckListItemId: norm.ElectricalCheckListItemId,
                    }))
                    || []
            );
        },
        [activeChecklist.isError, activeChecklist.data?.Items, activeChecklist.error?.Message, search.search, activeTab, organizationId],
    );

    const defaultListActivate = api.useMutation('put', '/api/v2/electrical/checklists/lists/activate');

    const defaultNorms = api.useMutation('post', '/api/v2/electrical/checklists/lists/createdefault');

    if (getActiveChecklist.isLoading) {
        return <LoadingPage />;
    }

    return (
        <div>
            <div className="w-full">
                <Button
                    variant="link"
                    className="flex items-center sm:hidden w-full mb-3 gap-1 border px-3 rounded-lg text-foreground hover:no-underline hover:bg-background"
                    onClick={() => {
                        setItem(null);
                        setOpenModal(true);
                    }}
                >
                    <PlusIcon className="w-5 h-5" />
                    {t('dashboard.settings.scope12.item')}
                </Button>
                <DataTable.TableHead
                    title={t('dashboard.settings.checklist.title')}
                    subtitle={t('dashboard.settings.checklist.subtitle')}
                    search={Route}
                />
                {/* buttons lg+ */}
                <div className="flex flex-row py-3 border px-5 w-full">
                    <div className="flex flex-row">
                        <div className="block">
                            {tabsKind.map((item, index) => (
                                <Link
                                    key={item.value}
                                    to="/dashboard/organizations/$organizationId/settings/visual-inspections"
                                    params={{ organizationId }}
                                    search={{
                                        tab: item.value as 'DistributionBoard' | 'PhotovoltaicInverter' | 'Machine' | 'Other' | 'FinalCircuit' | undefined,
                                        search: search?.search,
                                    }}
                                >
                                    <Button
                                        onClick={() => switchTab(item.value)}
                                        style={
                                            index === 0
                                                ? { borderRadius: '10px 0px 0px 10px ', borderLeft: '1px solid hsl(var(--border-primary)' }
                                                : index === tabsKind.length - 1
                                                    ? { borderRadius: '0px 10px 10px 0px' }
                                                    : { borderRadius: '0px' }
                                        }
                                        className={`border-border-primary border-t border-r border-b bg-background-active hover:bg-active ${item.value === activeTab ? ' text-secondary_hover font-bold' : 'text-secondary-700 bg-background-primary text-sm'}`}
                                        key={String(item.value)}
                                    >
                                        {item.label}
                                    </Button>
                                </Link>
                            ))}

                        </div>
                    </div>
                    <div className="sm:flex hidden flex-1 justify-end items-end">
                        <div className="flex flex-row-reverse gap-4">
                            <Button
                                variant="link"
                                className="flex items-center gap-1 border px-3 rounded-lg text-foreground hover:no-underline hover:bg-background"
                                onClick={() => {
                                    setItem(null);
                                    setOpenModal(true);
                                }}
                            >
                                <PlusIcon className="w-5 h-5" />
                                {t('dashboard.settings.scope12.item')}
                            </Button>
                            {currentUser?.IsAdmin && (
                                <Button
                                    variant="link"
                                    className="flex items-center w-32 gap-1 border px-3 rounded-lg text-foreground hover:no-underline hover:bg-background"
                                    onClick={async () => {
                                        if (await showConfirm('Weet je zeker dat je een standaardlijst wilt maken? Hierdoor wordt de huidige lijst gearchiveerd.')) {
                                            defaultNorms.mutateAsync({
                                                body: {
                                                    Description: 'Default Checklist',
                                                    OrganizationId: organizationId,
                                                },
                                            }).then((response) => {
                                                defaultListActivate.mutateAsync({
                                                    body: {
                                                        ElectricalCheckListId: response?.ElectricalCheckListId,
                                                    },
                                                }).then(() => {
                                                    toast('Aangemaakt. Reload.');
                                                });
                                            });
                                        }
                                    }}
                                >
                                    <PlusIcon className="w-5 h-5" />
                                    Default Lijst
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
                <DataTable
                    className="rounded-tl-none rounded-tr-none"
                    columns={tableColumns}
                    data={tableData}
                    pageSize={10}
                />
            </div>
            {
                isOpenModal
                && (
                    <ChecklistModal
                        isOpen={isOpenModal}
                        setIsOpen={setOpenModal}
                        ElectricalCheckListId={getActiveChecklist.data?.CheckLists[0]?.ElectricalCheckListId ?? null}
                        tabs={tabsKind}
                        checklist={item}
                    />
                )
            }
        </div>
    );
}
